import { useStore } from '@nanostores/react';
import { useLocation } from '@reach/router';
import { trackSelfDescribingEvent } from '@snowplow/browser-tracker';
import classNames from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';
import * as React from 'react';
import { useRef, useState } from 'react';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { AiOutlineExclamationCircle } from 'react-icons/ai';
import Select from 'react-select';

import VideoIcon from '@/components/provider-search/svg-components/video';
import { $moduleAbTest } from '@/stores/module-ab-test';
import { $navAbTest } from '@/stores/nav-ab-test';
import { $pageAbTest } from '@/stores/page-ab-test';
import { getCookie } from '@/utils/cookie-helpers';
import { mapEdgesToNodes } from '@/utils/helpers';
import { getSnowplowDuid } from '@/utils/snowplow';

import { MenuWithEnd } from '../menuWithEnd';
import { MultiValueContainer } from '../multiValueContainer';
import { CustomOption } from '../provider-search/custom-option';
import { GroupHeading } from '../provider-search/group-heading';
import { NoOptionsMessage } from '../provider-search/no-options-message';
import ChairIcon from '../provider-search/svg-components/chair';
import LocationIcon from '../provider-search/svg-components/location';
import MoneyIcon from '../provider-search/svg-components/money';
import * as styles from './stacks-provider-search.module.css';

const PARAM_LOCATION_KEYS = {
	lat: 'lt',
	long: 'lg',
	city: 'ct',
	state: 'st',
};

export const setSearchOptionsWithGroups = (searchField, type) => {
	const result = searchField.reduce((accumulator, fieldItem) => {
		const item = {
			...fieldItem,
			label: fieldItem.title,
		};
		const category =
			accumulator?.length &&
			accumulator.find(
				(option) =>
					option.label === fieldItem[`${type}Category`]?.categoryTitle
			);

		if (!category) {
			if (!fieldItem[`${type}Category`]?.categoryTitle) {
				return accumulator.concat(item);
			} else {
				const newCategory = {
					label: fieldItem[`${type}Category`]?.categoryTitle,
					description: fieldItem[`${type}Category`]?._rawDescription,
					tooltip: fieldItem[`${type}Category`]?.tooltip,
					options: [item],
				};
				return accumulator.concat(newCategory);
			}
		} else {
			category.options.push(item);
		}
		return accumulator;
	}, []);

	return result;
};

export const getLocationDetails = async (props) => {
	const { label } = props || {};
	var geocoder = new google.maps.Geocoder();
	var address = label;
	let locationQuery = '';
	let state = '';

	try {
		await geocoder.geocode(
			{
				address: address,
			},
			(results, status) => {
				if (status == google.maps.GeocoderStatus.OK) {
					state = results[0].address_components.find((address) =>
						address.types.includes('administrative_area_level_1')
					)?.short_name;
					const city = results[0].address_components.find((address) =>
						address.types.includes('locality')
					)?.short_name;

					const latitude = results[0].geometry.location.lat();
					const longitude = results[0].geometry.location.lng();

					locationQuery += latitude
						? `&${PARAM_LOCATION_KEYS.lat}=${btoa(latitude)}`
						: '';
					locationQuery += longitude
						? `&${PARAM_LOCATION_KEYS.long}=${btoa(longitude)}`
						: '';
					locationQuery +=
						city || label
							? `&${PARAM_LOCATION_KEYS.city}=${btoa(encodeURIComponent(city || label))}`
							: '';
					locationQuery += state
						? `&${PARAM_LOCATION_KEYS.state}=${btoa(state)}`
						: '';
				}
			}
		);
		return { locationQuery, state };
	} catch (error) {
		return { error };
	}
};

const generalStyles = {
	control: (styles, state) => {
		return {
			...styles,
			height: '48px',
			borderRadius: '0',
			marginBottom: '-1px',
			border: state.isFocused
				? '1px solid var(--focus-color)'
				: '1px solid #D4D1CB',
			zIndex: state.isFocused ? '10' : null,
			boxShadow: state.isFocused ? '0 0 0 5px #fff;' : null,
			outline: state.isFocused ? '3px solid var(--focus-color)' : null,
			'outline-offset': state.isFocused ? '2px' : null,
			textOverflow: 'ellipsis',
			textWrap: 'nowrap',
			input: {
				fontWeight: '300 !important',
			},
		};
	},
	option: (styles, state) => {
		return {
			...styles,
			'&:hover': {
				backgroundColor: '#F7F6F1',
			},
			'& label': {
				cursor: 'pointer',
			},
			padding: '12px 24px',
			color: '#02291C',
			backgroundColor: state.isFocused ? '#F7F6F1' : '#FFFFFF',
			boxShadow: state.isFocused ? '0 0 0 4px #F7F6F1' : null,
			cursor: 'pointer',
		};
	},
	menu: (styles) => ({
		...styles,
		zIndex: '300',
		width: '100%',
		maxWidth: '75vw',
	}),
};

const selectComponents = {
	DropdownIndicator: () => null,
	IndicatorSeparator: () => null,
	GroupHeading,
	NoOptionsMessage,
	MultiValueRemove: () => null,
	IndicatorsContainer: () => null,
	Option: CustomOption,
};

const searchTheme = (theme) => ({
	...theme,
	colors: {
		...theme.colors,
		primary: '#0C4A34',
		neutral50: '#7B756F',
	},
});

// TODO: for the love of all things organized lets refactor this when we get a chance 😂
export function StacksProviderSearch({
	abTestId,
	ctaAnalytics,
	insuranceVisibility,
	locationVisibility,
	servicesVisibility,
	sessionTypeVisibility,
	themeColor,
}) {
	// stores
	const moduleAbTest = useStore($moduleAbTest);
	const navAbTest = useStore($navAbTest);
	const pageAbTest = useStore($pageAbTest);

	const location = useLocation();

	const elid = getCookie('elid') ?? 'anonymous';

	// refs
	const googlePlacesRef = useRef();
	const insuranceSelectRef = useRef();
	const googlePlacesSelectRef = useRef();
	const sessionTypeSelectRef = useRef();

	const data = useStaticQuery(graphql`
		query Search {
			services: allSanityService(sort: { orderRank: ASC }) {
				edges {
					node {
						...Services
					}
				}
			}
			insurance: allSanityInsurer(
				sort: [
					{ insurerCategory: { orderRank: ASC } }
					{ orderRank: ASC }
				]
			) {
				edges {
					node {
						...Insurers
					}
				}
			}
			sessionType: allSanitySessionType(sort: { orderRank: ASC }) {
				edges {
					node {
						...SessionType
					}
				}
			}
		}
	`);

	const serviceNodes = (data || {}).services
		? mapEdgesToNodes(data.services)
		: [];
	const insuranceNodes = (data || {}).insurance
		? mapEdgesToNodes(data.insurance)
		: [];
	const sessionTypeNodes = (data || {}).sessionType
		? mapEdgesToNodes(data.sessionType)
		: [];
	const [serviceOptions] = useState(
		setSearchOptionsWithGroups(serviceNodes, 'service')
	);
	const [insurerOptions] = useState(
		setSearchOptionsWithGroups(insuranceNodes, 'insurer')
	);

	const [sessionTypeOptions] = useState(sessionTypeNodes);
	const [showLocationError, setShowLocationError] = useState(false);
	const [selectedServices, setSelectedServices] = useState(null);
	const [selectedInsurance, setSelectedInsurance] = useState(null);
	const [selectedLocation, setSelectedLocation] = useState(null);
	const [selectedSessionType, setSelectedSessionType] = useState(null);

	const handleSetOutOfPocket = () => {
		const outOfPocket = insuranceNodes.find(
			(insurer) => insurer.value === 'payment_out_of_pocket'
		);

		const transformedInsurer = {
			...outOfPocket,
			label: outOfPocket.title,
		};

		insuranceSelectRef.current.setValue(transformedInsurer);

		// manually open up the insurance dropdown to match secure site
		insuranceSelectRef.current.onMenuOpen();
	};

	const handleDropdownServiceSelect = (options) => {
		setSelectedServices(options);
	};

	const handleDropdownInsuranceSelect = (options) => {
		setSelectedInsurance(options);
	};

	const handleDropdownLocationSelect = (options) => {
		setShowLocationError(false);
		setSelectedLocation(options);
	};

	const handleDropdownSessionTypeSelect = (options) => {
		setSelectedSessionType(options);
	};

	const constructSearchUrl = async () => {
		if (!selectedLocation) {
			setShowLocationError(true);
			return;
		}

		const { locationQuery } = await getLocationDetails(selectedLocation);

		const servicesQuery = selectedServices
			?.map((service) => service.value)
			.join(',');
		const insuranceQuery = selectedInsurance?.value;
		const sessionTypeQuery = selectedSessionType?.value;
		const nonLocationValues = [];

		if (servicesQuery) {
			nonLocationValues.push(servicesQuery);
		}

		if (insuranceQuery) {
			nonLocationValues.push(insuranceQuery);
		}

		if (sessionTypeQuery) {
			nonLocationValues.push(sessionTypeQuery);
		}

		// get userid from snowplow and generate timestamp
		// normally this is done automatically in the snowplow tracker
		// we need manually append _sp params here because we are manually generating this url
		const duid = getSnowplowDuid();
		const { domain_userid } = duid;
		const timestamp = Date.now();

		const searchParams = location.search;

		const searchUrl = `https://secure.helloalma.com/providers-results/${searchParams ? `${searchParams}&` : '?'}filterables=${btoa(
			encodeURIComponent(`${nonLocationValues.join(',')},no_availability`)
		)}${locationQuery && locationQuery}&_sp=${domain_userid}.${timestamp}&ld_user_key=${elid}${pageAbTest?.active || moduleAbTest?.active || navAbTest?.active ? `&ab=${pageAbTest?.flagName || moduleAbTest?.flagName || navAbTest?.flagName}` : ''}`;

		// manually redirect to the secure site
		window.location.href = searchUrl;
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		trackSelfDescribingEvent({
			event: {
				schema: 'iglu:com.snowplowanalytics.snowplow/submit_form/jsonschema/1-0-0',
				data: {
					formId: 'provider_search',
					formClasses: [],
					elements: [],
				},
			},
			context:
				pageAbTest?.active || moduleAbTest?.active || navAbTest?.active
					? [
							{
								schema: 'iglu:com.helloalma/launch_darkly/jsonschema/2-0-0',
								data: {
									flag_name: pageAbTest?.active
										? pageAbTest?.flagName
										: moduleAbTest?.active
											? moduleAbTest?.flagName
											: navAbTest?.active
												? navAbTest?.flagName
												: null,
									flag_value: pageAbTest?.active
										? pageAbTest?.flagValue
										: moduleAbTest?.active
											? moduleAbTest?.flagValue
											: navAbTest?.active
												? navAbTest?.flagValue
												: null,
								},
							},
						]
					: [],
		});

		// handle GA4 event
		if (ctaAnalytics?.enableGA4) {
			if (window?.gtag) {
				window.gtag('event', ctaAnalytics?.ga4Event?.name, {
					send_to: 'G-R0Z6J0E1GE',
					cta_name: 'See therapists',
					cta_url: location?.href,
				});
			}
		}

		// construct search url
		constructSearchUrl();
	};

	return (
		<section
			className={classNames(
				styles.stacksProviderSearch,
				styles[themeColor]
			)}
		>
			<form className={styles.form} autoComplete='on'>
				{servicesVisibility && (
					<div className={styles.dropdownItem}>
						<ChairIcon className={styles.icon} />
						<Select
							components={{
								...selectComponents,
								ValueContainer: MultiValueContainer,
							}}
							tabSelectsValue={false}
							name={'services'}
							useCheckbox={true}
							isSearchable={false}
							closeMenuOnSelect={false}
							hideSelectedOptions={false}
							isMulti={true}
							options={serviceOptions}
							theme={searchTheme}
							styles={{
								...generalStyles,
								valueContainer: (styles) => ({
									...styles,
									display: 'flex',
									flexWrap: 'nowrap',
									paddingLeft: '40px',
									cursor: 'pointer',
									'@media only screen and (max-width: 999px)':
										{
											paddingLeft: '2.5em',
										},
									textOverflow: 'ellipsis',
									overflow: 'hidden',
								}),
								control: (styles, state) => {
									return {
										...styles,
										height: '48px',
										marginBottom: '-1px',
										borderRadius: '4px 4px 0 0',
										border: state.isFocused
											? '1px solid var(--focus-color)'
											: '1px solid #D4D1CB',
										zIndex: state.isFocused ? '10' : null,
										boxShadow: state.isFocused
											? '0 0 0 5px #fff;'
											: null,
										outline: state.isFocused
											? '3px solid var(--focus-color)'
											: null,
										'outline-offset': state.isFocused
											? '2px'
											: null,
										input: {
											fontWeight: '300 !important',
										},
									};
								},
							}}
							ariaMultiLine={false}
							onKeyDown={(e) => {
								if (e.key === 'Backspace')
									setSelectedServices(null);
							}}
							onChange={(options) =>
								handleDropdownServiceSelect(options)
							}
							value={selectedServices}
							placeholder='Type of service'
						/>
					</div>
				)}
				{insuranceVisibility && (
					<div className={styles.dropdownItem}>
						<MoneyIcon className={styles.icon} />
						<Select
							components={{ ...selectComponents }}
							tabSelectsValue={false}
							theme={searchTheme}
							name={'insurance'}
							addSeparatorOnFirstElement={true}
							ref={insuranceSelectRef}
							options={insurerOptions}
							handleSetOutOfPocket={handleSetOutOfPocket}
							styles={{
								...generalStyles,
								valueContainer: (styles) => ({
									...styles,
									paddingLeft: '40px',
									cursor: 'text',
									'@media only screen and (max-width: 999px)':
										{
											paddingLeft: '2.5em',
										},
									textOverflow: 'ellipsis',
								}),
							}}
							onKeyDown={(e) => {
								if (e.key === 'Backspace')
									setSelectedInsurance(null);
							}}
							onChange={(options) => {
								insuranceSelectRef.current?.blur();
								insuranceSelectRef.current?.focus();
								handleDropdownInsuranceSelect(options);
							}}
							value={selectedInsurance}
							placeholder='Insurance'
						/>
					</div>
				)}
				{locationVisibility && (
					<div className={styles.dropdownItem}>
						<LocationIcon className={styles.icon} />
						<div className={styles.dropdownItem}>
							<GooglePlacesAutocomplete
								apiKey={process.env.GATSBY_GOOGLE_API_KEY}
								ref={googlePlacesRef}
								selectProps={{
									required: true,
									theme: searchTheme,
									tabSelectsValue: false,
									openMenuOnClick: false,
									placeholder: 'Your city or ZIP*',
									value: selectedLocation,
									name: 'locations',
									ref: googlePlacesSelectRef,
									locationContainerDisclaimer:
										"A provider needs to be licensed in the state you're located in when sessions are happening. Make sure to choose the appropriate state.",
									components: {
										DropdownIndicator: () => null,
										IndicatorSeparator: () => null,
										GroupHeading,
										NoOptionsMessage,
										MultiValueRemove: () => null,
										IndicatorsContainer: () => null,
										Option: (props) => (
											<CustomOption address {...props} />
										),
										Menu: MenuWithEnd,
									},
									onChange: (options) => {
										googlePlacesSelectRef.current?.blur();
										googlePlacesSelectRef.current?.focus();
										handleDropdownLocationSelect(options);
									},
									onKeyDown: (e) => {
										if (e.key === 'Backspace')
											setSelectedLocation(null);
									},
									styles: {
										...generalStyles,
										valueContainer: (styles) => ({
											...styles,
											paddingLeft: '40px',
											cursor: 'text',
											'@media only screen and (max-width: 999px)':
												{
													paddingLeft: '2.5em',
												},
											textOverflow: 'ellipsis',
										}),
										control: (styles, state) => {
											const borderActive =
												state.isFocused ||
												showLocationError;
											const borderColor =
												showLocationError
													? '#994731'
													: '#2f5b3d';

											return {
												...styles,
												height: '48px',
												borderRadius: '0',
												marginBottom: '-1px',
												'@media only screen and (min-width: 1000px)':
													{
														...styles[
															'@media only screen and (min-width: 1000px)'
														],
													},
												border: state.isFocused
													? '1px solid var(--focus-color)'
													: borderActive
														? `2px solid ${borderColor}`
														: '1px solid #D4D1CB',
												zIndex: state.isFocused
													? '10'
													: null,
												boxShadow: state.isFocused
													? '0 0 0 5px #fff;'
													: null,
												outline: state.isFocused
													? '3px solid var(--focus-color)'
													: null,
												'outline-offset':
													state.isFocused
														? '2px'
														: null,
												input: {
													fontWeight:
														'300 !important',
												},
											};
										},
									},
								}}
								autocompletionRequest={{
									componentRestrictions: {
										country: ['us'],
									},
									types: ['(regions)'],
								}}
							/>
							{showLocationError && (
								<span className={styles.locationError}>
									<AiOutlineExclamationCircle />
									Please enter a location
								</span>
							)}
						</div>
					</div>
				)}
				{sessionTypeVisibility && (
					<div className={styles.dropdownItem}>
						<VideoIcon className={styles.icon} />
						<Select
							components={selectComponents}
							name={'sessionTypes'}
							theme={searchTheme}
							ref={sessionTypeSelectRef}
							options={sessionTypeOptions.map((service) => ({
								value: service.value,
								label: service.title,
							}))}
							onKeyDown={(e) => {
								if (e.key === 'Backspace')
									setSelectedSessionType(null);
							}}
							onChange={(options) => {
								sessionTypeSelectRef.current?.blur();
								sessionTypeSelectRef.current?.focus();
								handleDropdownSessionTypeSelect(options);
							}}
							value={selectedSessionType}
							styles={{
								...generalStyles,
								valueContainer: (styles) => ({
									...styles,
									cursor: 'text',
									paddingLeft: '40px',
									'@media only screen and (max-width: 999px)':
										{
											paddingLeft: '2.5em',
										},
								}),
								control: (styles, state) => {
									return {
										...styles,
										height: '48px',
										borderRadius: '0 0 4px 4px ',
										'@media only screen and (min-width: 1000px)':
											{
												...styles[
													'@media only screen and (min-width: 1000px)'
												],
											},
										textOverflow: 'ellipsis',
										border: state.isFocused
											? '1px solid var(--focus-color)'
											: '1px solid #D4D1CB',
										zIndex: state.isFocused ? '10' : null,
										boxShadow: state.isFocused
											? '0 0 0 5px #fff;'
											: null,
										outline: state.isFocused
											? '3px solid var(--focus-color)'
											: null,
										'outline-offset': state.isFocused
											? '2px'
											: null,
									};
								},
							}}
							placeholder='Virtual or in-person'
						/>
					</div>
				)}
			</form>
			<button
				id={abTestId}
				className={classNames(styles.providerSearchButton, styles.dark)}
				onClick={handleSubmit}
			>
				See therapists
			</button>
		</section>
	);
}
